import { config } from '@lingoda/config';
import { getCookieValue } from '@lingoda/cookie';
import { captureError } from '@lingoda/monitor';
import { getTopLevelDomain } from '@lingoda/urls';
import type { EverflowConversionOptions, EverflowSDK } from '@everflow/everflow-sdk';

// Everflow advertiser event ids, mirroring the GTM tags (registration and placement test
// intentionally share the same id)
const ADV_EVENT_REGISTRATION = 1;
const ADV_EVENT_PLACEMENT_TEST = 1;

interface EverflowPurchase {
    amount: number;
    orderId: string;
    currency: string;
    productType: string;
    productName: string;
    couponCode: string;
    userId?: number;
}

export const trackEverflowPurchase = ({
    amount,
    orderId,
    currency,
    productType,
    productName,
    couponCode,
    userId,
}: EverflowPurchase) =>
    trackConversion({
        amount,
        order_id: orderId,
        coupon_code: couponCode,
        adv1: productType,
        adv2: productName,
        adv3: userId === undefined ? undefined : `${userId}`,
        parameters: { currency },
    });

export const trackEverflowRegistration = () =>
    trackConversion({ adv_event_id: ADV_EVENT_REGISTRATION });

export const trackEverflowPlacementTest = () =>
    trackConversion({ adv_event_id: ADV_EVENT_PLACEMENT_TEST });

const trackConversion = (options: EverflowConversionOptions) => {
    const advertiserId = Number(config.everflowAdvertiserId);

    // No-op when the config is absent (unit tests mock lgConfig as an empty object)
    if (!config.everflowTrackingDomain || !advertiserId) {
        return;
    }

    if (!hasTrackingConsent()) {
        return;
    }

    void sendConversion(advertiserId, options);
};

const sendConversion = async (advertiserId: number, options: EverflowConversionOptions) => {
    try {
        const EF = await loadSdk();

        void EF.conversion({
            aid: advertiserId,
            transaction_id: EF.getAdvertiserTransactionId(advertiserId),
            ...options,
        });
    } catch (error) {
        // Tracking must never break the calling flow (e.g. a failed chunk load)
        captureError(error);
    }
};

let sdkPromise: Promise<EverflowSDK> | undefined;

// The SDK is imported dynamically to keep it out of the app bundles: it's a side-effectful
// UMD build that cannot be tree-shaken, and only converting sessions ever need it
const loadSdk = () => {
    sdkPromise ??= import('@everflow/everflow-sdk').then(({ default: EF }) => {
        EF.configure({
            tracking_domain: config.everflowTrackingDomain,
            // Shared with the click recorded on the marketing website (CMS)
            tld: getTopLevelDomain(),
        });

        return EF;
    });

    return sdkPromise;
};

// Consent check applies on production only — local/release have no CookieYes banner
const hasTrackingConsent = () => {
    if (config.deployEnvironment !== 'production') {
        return true;
    }

    return (getCookieValue('cookieyes-consent') ?? '').includes('advertisement:yes');
};
